import React, { useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';

import DateIcon from 'assets/myAppointments/appointmentsList/date.svg';
import TimeIcon from 'assets/myAppointments/appointmentsList/time.svg';
import CustomDatePicker from 'common-ui/customDatePicker';
import CustomTimePicker from 'common-ui/customTimePicker';
import Modal from 'common-ui/modal';
import CustomSelectComponent from 'common-ui/select/Select';
import TextArea from 'common-ui/textArea';
import dayjs from 'dayjs';
import { createFollowUp } from 'features/newsales/newsalesActions';
import { getUsersDropdown } from 'features/users/usersActions';

import {
  ButtonRow,
  CancelButton,
  ErrorMessage,
  FormField,
  FormRow,
  Label,
  NoteSection,
  SubmitButton,
} from './FollowUpModal.styles';

const FollowUpModal = ({ isOpen, onClose, leadId, onSuccess, followUpData }) => {
  const dispatch = useDispatch();
  const [nextContactDate, setNextContactDate] = useState(null);
  const [nextContactTime, setNextContactTime] = useState('');
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [recipientOptions, setRecipientOptions] = useState([]);
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      if (followUpData) {
        setNextContactDate(dayjs(followUpData.nextContactDate));
        setNextContactTime(followUpData.nextContactTime || '');
        setNotes(followUpData.notes || '');
      } else {
        setNextContactDate(null);
        setNextContactTime('');
        setSelectedRecipient(null);
        setNotes('');
      }

      setError('');
      setIsSubmitting(false);
      fetchUsers();
    }
  }, [isOpen, followUpData]);

  const fetchUsers = async () => {
    try {
      const response = await dispatch(getUsersDropdown()).unwrap();

      const options =
        response?.users?.map((user) => ({
          value: user.uuid,
          label: `${user.name}${user.surname ? ` ${user.surname}` : ''}${user.defaultRole?.name ? ` - ${user.defaultRole.name}` : ''}`,
          rawData: user,
        })) || [];

      setRecipientOptions(options);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setRecipientOptions([]);
    }
  };

  const formatDateForAPI = (dateValue) => {
    if (!dateValue || dateValue === '') return null;
    const date = new Date(dateValue);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const validateForm = () => {
    if (!nextContactDate || nextContactDate === '') {
      setError('Next contact date is required');
      return false;
    }

    const selectedDate = new Date(nextContactDate);
    selectedDate.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (selectedDate < today) {
      setError('Next contact date cannot be in the past');
      return false;
    }

    if (!nextContactTime || nextContactTime === '') {
      setError('Next contact time is required');
      return false;
    }

    const isToday = selectedDate.getTime() === today.getTime();
    if (isToday) {
      const timeDayjs = dayjs(nextContactTime);
      const now = dayjs();

      if (timeDayjs.isBefore(now) || timeDayjs.isSame(now)) {
        setError('Time must be in the future for today');
        return false;
      }
    }

    if (!selectedRecipient) {
      setError('Please select a recipient');
      return false;
    }

    if (notes && notes.length > 200) {
      setError('Notes cannot exceed 200 characters');
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    setError('');

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const timeFormatted = nextContactTime ? dayjs(nextContactTime).format('HH:mm') : '';

      const followUpData = {
        nextContactDate: formatDateForAPI(nextContactDate),
        nextContactTime: timeFormatted,
        recipientType: 'user',
        recipientId: selectedRecipient.value,
      };

      if (notes && notes.trim()) {
        followUpData.notes = notes.trim();
      }

      await dispatch(
        createFollowUp({
          leadId,
          followUpData,
        })
      ).unwrap();

      if (onSuccess) {
        onSuccess();
      }

      onClose();
    } catch (err) {
      setError(err?.message || err || 'Failed to create follow-up');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Create Follow-Up"
      footer={false}
      width="550px"
    >
      <FormRow>
        <FormField>
          <Label className="required">Next Contact Date</Label>
          <CustomDatePicker
            value={nextContactDate}
            onChange={setNextContactDate}
            placeholder="Select date"
            height="48px"
            disabled={isSubmitting}
            minDate={dayjs()}
            icon={DateIcon}
          />
        </FormField>

        <FormField>
          <Label className="required">Next Contact Time</Label>
          <CustomTimePicker
            value={nextContactTime}
            onChange={setNextContactTime}
            placeholder="HH:MM"
            views={['hours', 'minutes']}
            height="48px"
            disabled={isSubmitting}
            icon={TimeIcon}
          />
        </FormField>
      </FormRow>

      <FormRow style={{ marginTop: '16px' }}>
        <FormField>
          <CustomSelectComponent
            label="Recipient"
            req={true}
            options={recipientOptions}
            value={selectedRecipient}
            onChange={setSelectedRecipient}
            placeholder="Select recipient"
            isSearchable
            isClearable
            noOptionsMessage="No users found"
          />
        </FormField>
      </FormRow>

      <NoteSection>
        <Label>Notes</Label>
        <TextArea
          placeholder="Add notes (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          maxLength={200}
          resizeVertical={false}
          resizeHorizontal={false}
          disabled={isSubmitting}
        />
      </NoteSection>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <ButtonRow>
        <CancelButton onClick={onClose} disabled={isSubmitting}>
          Cancel
        </CancelButton>
        <SubmitButton onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? 'Creating...' : 'Create Follow-Up'}
        </SubmitButton>
      </ButtonRow>
    </Modal>
  );
};

export default FollowUpModal;
